import React, { useState } from "react";
import {
    Card,
    Avatar,
    Button,
    Form,
    Input,
    Upload,
    Row,
    Col,
} from "antd";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    EditOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import SideBar from "../component/SideBar";
import secureLocalStorage from "react-secure-storage";
import { toast } from "react-toastify";
import axios from "axios";

const Profile = () => {
    const user = JSON.parse(secureLocalStorage.getItem("user"));
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const [imageFile, setImageFile] = useState(null);
    const [imageUrl, setImageUrl] = useState(user?.photo || null);

    const uploadProps = {
        beforeUpload: (file) => {
            const isImage = file.type.startsWith("image/");
            if (!isImage) {
                toast.error("You can only upload image files!");
                return Upload.LIST_IGNORE;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = () => setImageUrl(reader.result);
            reader.readAsDataURL(file);
            return false;
        },
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("first_name", values.first_name);
            formData.append("last_name", values.last_name);
            formData.append("phone", values.phone);
            formData.append("address", values.address);
            formData.append("city", values.city);
            formData.append("state", values.state);
            formData.append("country", values.country);
            formData.append("zip", values.zip);

            if (imageFile) {
                formData.append("photo", imageFile);
            }
            const userId = user?.id;
            const res = await axios.post(`${process.env.REACT_APP_API_URL}api/update-user-data/${userId}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (res.data.status === true) {
                toast.success(res.data.message);
                secureLocalStorage.setItem("user", JSON.stringify(res.data.data));
                setTimeout(() => { window.location.reload(); }, 1000)
            } else {
                toast.error(res.data.message || "Something went wrong!");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to update profile!");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="flex min-h-screen bg-gray-100 pt-[160px] pb-[50px]">
            <div className="w-64 bg-brandBlue shadow-lg py-8 px-4 text-white rounded-tr-[10px] rounded-br-[10px]">
                <h2 className="text-2xl font-bold mb-10">My Account</h2>
                <SideBar />
            </div>

            <div className="flex-1 p-10 pt-0 pb-0">
                <Card className="w-full shadow-xl rounded-2xl">
                    <div className="flex items-center mb-6 gap-4">
                        <Avatar
                            size={120}
                            src={imageUrl}
                            icon={<UserOutlined />}
                            className="border-2 border-brandBlue"
                        />
                        <div>
                            <h1 className="text-2xl font-bold text-brandBlue">
                                Edit Profile
                            </h1>
                            <p className="text-gray-500 mb-2">
                                Update your personal information
                            </p>
                            <Upload {...uploadProps} showUploadList={false}>
                                <Button
                                    icon={<UploadOutlined />}
                                    className="mt-3 border-brandBlue text-brandBlue"
                                >
                                    Change Photo
                                </Button>
                            </Upload>
                        </div>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            first_name: user?.fname || "",
                            last_name: user?.lname || "",
                            phone: user?.phone || "",
                            address: user?.address || "",
                            city: user?.city || "",
                            state: user?.state || "",
                            country: user?.country || "",
                            zip: user?.zip || "",
                        }}
                        onFinish={handleSubmit}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="First Name"
                                    name="first_name"
                                    rules={[{ required: true, message: "Enter first name" }]}
                                >
                                    <Input prefix={<UserOutlined />} placeholder="Enter first name" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Last Name"
                                    name="last_name"
                                    rules={[{ required: true, message: "Enter last name" }]}
                                >
                                    <Input prefix={<UserOutlined />} placeholder="Enter last name" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Email"
                                    name="email"
                                    initialValue={user?.email}
                                >
                                    <Input prefix={<MailOutlined />} disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Phone Number" name="phone">
                                    <Input
                                        prefix={<PhoneOutlined />}
                                        placeholder="Enter phone number"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item label="Address" name="address">
                            <Input.TextArea rows={2} placeholder="Enter address" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Country" name="country">
                                    <Input placeholder="Enter country" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="City" name="city">
                                    <Input placeholder="Enter city" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="State" name="state">
                                    <Input placeholder="Enter state" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Postal Code" name="zip">
                                    <Input placeholder="Enter postal code" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <div className="flex justify-end gap-3 mt-6">
                            <Button className="px-6">Cancel</Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                className="bg-brandBlue border-none px-6"
                                icon={<EditOutlined />}
                            >
                                Update
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </div>
    );
};

export default Profile;
